import { SolicitudStepper } from "@/components/ui/solicitud-stepper";
import type { SolicitudDetalle } from "@/lib/types";
import { useAuthStore } from "@/lib/auth-store";
import { PERMISOS } from "@/lib/permisos";
import { estaEnEsperaDeAutorizacion } from "@/features/solicitud-detalle/utils/estado-flags";
import { EsperaDeAutorizacionView } from "./EsperaDeAutorizacionView";
import { DetalleTabs } from "./DetalleTabs";
import { DetalleSidebar } from "./DetalleSidebar";

/** Mapea la `key` del paso del stepper al id de su sección en el resumen. */
const ANCLA_POR_PASO: Record<string, string> = {
  crear: "sec-organizacion",
  organizacion: "sec-organizacion",
  contrato: "sec-contrato",
  orden: "sec-orden",
  cfdi: "sec-cfdi",
  conciliacion: "sec-cfdi",
  datos: "sec-datos",
  proveedor: "sec-proveedor",
  prorrateo: "sec-prorrateo",
  documentos: "sec-documentos",
};

export function DetalleLayout({ sol }: { sol: SolicitudDetalle }) {
  // Solo quien puede GESTIONAR solicitudes navega por los pasos del stepper
  // (que llevan a las rutas de captura). Un viewer de solo lectura (Visor) o de
  // consulta (Mesa de Control/Auditor) ve el detalle pero al pinchar un paso
  // caería en un endpoint de captura que exige `solicitudes.gestionar` → 403.
  const permiteNavegar = useAuthStore((s) => (s.user?.permisos ?? []).includes(PERMISOS.solicitudesGestionar));
  // Quien ACTÚA sobre el flujo/solicitud ve la vista enfocada (flujo + chat)
  // cuando está en autorización. Un viewer de solo lectura ve en su lugar el
  // detalle CON PESTAÑAS (Resumen con datos generales/proveedor/CFDI/prorrateo/
  // documentos + Autorización + Chat), para poder consultar todo el contenido.
  const puedeActuar = useAuthStore((s) => {
    const p = s.user?.permisos ?? [];
    return (
      p.includes(PERMISOS.autorizacionAutorizar) ||
      p.includes(PERMISOS.solicitudesGestionar) ||
      p.includes(PERMISOS.contrapagosGestionar)
    );
  });
  const enEspera = estaEnEsperaDeAutorizacion(sol.estado) && puedeActuar;

  // En modo solo-lectura, al pinchar un paso del stepper hacemos scroll a la
  // sección correspondiente del resumen (misma página) en vez de navegar.
  const scrollASeccion = (stepKey: string) => {
    const id = ANCLA_POR_PASO[stepKey];
    if (!id) return;
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="order-2 col-span-12 md:col-span-4 lg:order-1 lg:col-span-3">
        <div className="sticky top-[72px]">
          <SolicitudStepper
            estado={sol.estado}
            subtipo={sol.subtipo}
            tipo={sol.tipo}
            prorrateoAutomatico={sol.prorrateoAutomatico}
            solicitudId={sol.id}
            contabilizada={sol.enviadaAContabilizacion}
            motivos={sol.motivosBaja}
            dadaDeBajaPorNombre={sol.dadaDeBajaPorNombre}
            permiteNavegar={permiteNavegar}
            hrefSoloLectura={`/solicitudes/${sol.id}`}
            onStepSoloLectura={permiteNavegar ? undefined : scrollASeccion}
          />
        </div>
      </div>
      <div className="order-1 col-span-12 md:col-span-8 lg:order-2 lg:col-span-6">
        {enEspera ? (
          <EsperaDeAutorizacionView sol={sol} />
        ) : (
          <DetalleTabs sol={sol} defaultTab={puedeActuar ? undefined : "resumen"} />
        )}
      </div>
      <div className="order-3 col-span-12 lg:col-span-3">
        <DetalleSidebar sol={sol} />
      </div>
    </div>
  );
}
